import { colors, Tabs, utils } from '@prenuvo/shared-library';
import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';

import config from '../config';
import { useAuth, useFilter, useTable } from '../hooks';
import { TabsProps } from '../types';

export interface Tab {
  label: string;
}

export interface NavigationTabsProps {
  options: TabsProps[];
  selectedTab: string;
  handleTabChange: (tab: string) => void;
}

const Header = styled.div`
  background-color: ${colors.pearl};
  border-top: 1px solid ${colors.cloud};
  border-bottom: 3px solid ${colors.athensgray};
`;

const Wrapper = styled.div`
  margin: 0 auto;
  max-width: 1440px;
  display: flex;
  justify-content: space-between;
  position: relative;
`;

const TabWrapper = styled.div`
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: center;
  padding: 0 20px;
`;

export const NAVIGATION_TABS = { 
  referrals: { label: 'ARs & Referrals', id: 'referrals' },
  consultations: { label: 'Consultations', id: 'consultations'},
  prescriptions: { label: 'Prescriptions', id: 'prescriptions'},
};

const NavigationTab = ({ options, selectedTab, handleTabChange }: NavigationTabsProps) => {
  const { indicators, getIndicators } = useTable();

  useEffect(() => {
    getIndicators();
  }, []);

  return (
    <div>
      <Tabs initialTab={selectedTab} onChange={handleTabChange}>
        <Tabs.TabList variant={'secondary'}>
          {options.map((tab) => (
            <Tabs.Tab key={tab.id} id={tab.id} variant={'secondary'}>
              {utils.capitalize(tab.label)}
              {indicators[tab.id] && indicators[tab.id].indicatorNumber > 0 && (
                <Tabs.Indicator
                  indicatorNumber={indicators[tab.id].indicatorNumber}
                  color={indicators[tab.id].color}
                />
              )}
            </Tabs.Tab>
          ))}
        </Tabs.TabList>
      </Tabs>
    </div>
  );
};

const DEFAULT_TAB = NAVIGATION_TABS.referrals

const Navigation = () => {
  const { isNPCoordinatorOrPrescriber } = useAuth();
  const tabOptions = isNPCoordinatorOrPrescriber
    ? [NAVIGATION_TABS.referrals, NAVIGATION_TABS.consultations, NAVIGATION_TABS.prescriptions]
    : [NAVIGATION_TABS.referrals, NAVIGATION_TABS.consultations];
  const { setStatus } = useFilter();
  const { setIsOpen, navigateWithView } = useTable();
  const [selectedTab, setSelectedTab] = useState('');
  const { tab, studyId, innerTab } = useParams<{
    tab: string;
    studyId: string;
    innerTab: string;
  }>();

  const setDefaultStatus = (tabId: string) => {
    if (tabId === NAVIGATION_TABS.referrals.id) {
      setStatus('requiring-referrals');
    }
    if (tabId === NAVIGATION_TABS.consultations.id) {
      setStatus('requiring-consult');
    }
    if (tabId === NAVIGATION_TABS.prescriptions.id) {
      setStatus('prescription-pending');
    }
  };

  const getNavigateUrl = (tabId: string) => {
    if (studyId && innerTab && tabId === tab) {
      return `${config.BASE_NAME}/${tabId}/study/${studyId}/${innerTab}`;
    }
    if (tabOptions.find((tab) =>  tab.id === tabId)) {
      return `${config.BASE_NAME}/${tabId}`;
    }
    if (tabId === 'prescriptions' && !isNPCoordinatorOrPrescriber) {
      return '/401';
    }
    if (!tabId) {
      return `${config.BASE_NAME}/${DEFAULT_TAB.id}`;
    }
    return '/404';
  };

  const handleTabChange = useCallback((tabId: string) => {
    navigateWithView(getNavigateUrl(tabId), true);
    if (studyId && innerTab) {
      setIsOpen(true);
    } else {
      setIsOpen(false);
    }
    setDefaultStatus(tabId);
    setSelectedTab(tabId);
  }, []);

  useEffect(() => {
    if (!tab) {
      handleTabChange(DEFAULT_TAB.id);
    }
    if (tab !== selectedTab) {
      handleTabChange(tab as string);
    }
  }, [tab]);

  return (
    <Header>
      <Wrapper>
        <TabWrapper>
          <NavigationTab
            options={tabOptions}
            selectedTab={selectedTab}
            handleTabChange={handleTabChange}
          />
        </TabWrapper>
      </Wrapper>
    </Header>
  );
};

export default Navigation;
